// frontend/src/context/NotificationContext.js
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { App as AntApp } from 'antd';

// Create the context
const NotificationContext = createContext(null);

// This is the provider component that will wrap our app
export const NotificationProvider = ({ children }) => {
    const { message, modal, notification } = AntApp.useApp();
    const lastErrorTime = useRef(0);

    useEffect(() => {
        const handleConnectionError = () => {
            const now = Date.now();
            // Throttle to avoid multiple notifications for parallel failed requests (5 second cooldown)
            if (now - lastErrorTime.current > 5000) {
                lastErrorTime.current = now;
                notification.error({
                    message: 'Server Connection Error',
                    description: 'Unable to communicate with the server. Please check your internet connection or try again later.',
                    placement: 'bottomRight',
                    duration: 10,
                });
            }
        };

        window.addEventListener('backend-connection-error', handleConnectionError);
        return () => window.removeEventListener('backend-connection-error', handleConnectionError);
    }, [notification]);

    // We create a new object with the familiar API, but using the hooked instances
    const api = {
        message: {
            success: (content) => message.success(content),
            error: (content) => message.error(content),
            warning: (content) => message.warning(content),
            // You can add more methods if you need them
        },
        modal: {
            confirm: (config) => modal.confirm(config),
            // Add others like .info, .success if needed
        },
        notification
    };

    return (
        <NotificationContext.Provider value={api}>
            {children}
        </NotificationContext.Provider>
    );
};

// This is the custom hook that our components will use
export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};